import { Injectable } from '@angular/core';
import { GlobalAnalyticsMapperUtil, TealiumAnalyticsEvent, TealiumAnalyticsService, TealiumConstants } from '@ddt-web/analytics';
import { InjectConfig, PaConfigKey } from '@ddt-web/config-layer';
import { PartnerPlatformControlsList } from '@ddt-web/domain';
import { InjectLocalStorage, LocalStorageKey } from '@ddt-web/generic-utils';
import { ApplicationStateStoreProxyService, PartnerAuthentication, PartnerAuthenticationInfo, PartnerPlatform } from '@ddt-web/shared-stores';
import * as isEmpty from 'lodash/isEmpty';
import { Observable, of } from 'rxjs';
import { first, map, take, tap } from 'rxjs/operators';

import { PartnerPortfoliosDataService } from './partner-portfolios-data.service';

@Injectable({
	providedIn: 'root'
})
export class PartnerPortfoliosLoginService {
	@InjectConfig(PaConfigKey.PARTNER_PLATFORM_CONTROLS_LIST) public readonly partnerPlatformControlsList: PartnerPlatformControlsList;
	@InjectLocalStorage(LocalStorageKey.ExternalPartner) private externalPartnerLS: any;

	constructor(
		private partnerPortfoliosDataService: PartnerPortfoliosDataService,
		private applicationStateStoreProxyService: ApplicationStateStoreProxyService,
		private tealiumAnalyticsService: TealiumAnalyticsService
	) { }

	/**
	 * Checks if the user is authenticated with the selected partner platform.
	 * If not, initiates the login process.
	 * The authentication state is stored in the application state store and in local storage.
	 */
	public checkAuthentication(selectedPartner: PartnerPlatform): Observable<PartnerAuthenticationInfo> {
		if (this.externalPartnerLS?.[selectedPartner.id]?.timeout > Date.now()) {
			return of(this.externalPartnerLS?.[selectedPartner.id]);
		} else {
			// User is not authenticated, initiate login
			this.initiatePartnerLogin(selectedPartner);
			return this.applicationStateStoreProxyService.getPartnerAuthenticationState().pipe(
				first(partnerAuthentication => !isEmpty(partnerAuthentication?.[selectedPartner.id])),
				tap((partnerAuthentication: PartnerAuthentication) => {
					if (partnerAuthentication[selectedPartner.id]?.isAuthenticated) {
						this.externalPartnerLS = {
							...(this.externalPartnerLS || {}),
							[selectedPartner.id]: {
								...partnerAuthentication[selectedPartner.id],
								timeout: Date.now() + this.partnerPlatformControlsList[selectedPartner.id].timeoutSecs * 1000
							}
						};
					}
				}),
				map(partnerAuthentication => partnerAuthentication[selectedPartner.id])
			);
		}
	}

	public initiatePartnerLogin(selectedPartner: PartnerPlatform): void {
		// clear the store before login
		this.applicationStateStoreProxyService.updatePartnerAuthenticationInfo(selectedPartner.id, {} as PartnerAuthenticationInfo);

		const popupWindow = this.partnerPlatformControlsList?.[selectedPartner.id]?.popupWindow;
		const childLoginWindow = this.showPopupWindow(
			selectedPartner.authorizationUri,
			'partnerLoginWindow',
			window,
			popupWindow?.width ?? 500,
			popupWindow?.height ?? 300
		);
		const pollInterval = setInterval(() => {
			if (childLoginWindow?.closed) {
				try {
					const urlParams = new URLSearchParams(childLoginWindow?.location?.search);
					const authCode = urlParams.get('code');
					if (authCode) {
						this.initiateSession(authCode, selectedPartner.id);
					} else {
						this.trackPartnerAuthenticationStatus(selectedPartner.id, false);
						this.applicationStateStoreProxyService.updatePartnerAuthenticationInfo(selectedPartner.id, { isAuthenticated: false, apiErrors: false });
					}
				} catch (error) {
					console.error(error);
					this.trackPartnerAuthenticationStatus(selectedPartner.id, false);
					this.applicationStateStoreProxyService.updatePartnerAuthenticationInfo(selectedPartner.id, { isAuthenticated: false, apiErrors: false });
				}

				clearInterval(pollInterval);
			}
		}, 1000); // Poll every 1 second
	}

	private initiateSession(authCode: string, selectedPartnerId: string): void {
		this.partnerPortfoliosDataService.exchangeAuthCode(authCode, selectedPartnerId).pipe(take(1)).subscribe(res => {
			this.trackPartnerAuthenticationStatus(selectedPartnerId, res);
			this.applicationStateStoreProxyService.updatePartnerAuthenticationInfo(selectedPartnerId, { isAuthenticated: res, apiErrors: !res });
		});
	}

	private showPopupWindow(url, windowName, parentWindow, width, height) {
		const y = parentWindow.top.outerHeight / 2 + parentWindow.top.screenY - (height / 2);
		const x = parentWindow.top.outerWidth / 2 + parentWindow.top.screenX - (width / 2);
		return parentWindow.open(url, windowName,
			`toolbar=no, location=no, directories=no, status=no, menubar=no, width=${width}, height=${height}, top=${y}, left=${x}`
		);
	}

	private trackPartnerAuthenticationStatus(partnerId: string, isAuthenticated: boolean): void {
		const authStatus: string = isAuthenticated ? 'successful' : 'unsuccessful';
		this.tealiumAnalyticsService.trackEventTealium(
			new TealiumAnalyticsEvent(`${TealiumConstants.IMPORT_PARTNER_PORTFOLIO_EVENTS.PARTNER_AUTHENTICATION}${partnerId}:${authStatus}`, {})
		);

		GlobalAnalyticsMapperUtil.setImportPartnerName(isAuthenticated ? partnerId : undefined);
	}
}
